import React, { ChangeEvent, useEffect, useState } from 'react';
import { FieldValues, FormState, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { DateInput, FormField, Label } from '@anwb/poncho/components/forms';
import { useGaFunnelInfo } from 'hooks/useGaFunnelInfo';

import { plCustomerSelector } from '../../../../../store/selectors';
import { PLCustomerData } from '../../../../../types';
import { formatDateValue, getMaxBirthdate, handleFormGaEvent, validateBirthdate } from '../utils';

type DateInputFieldProps = {
	register: UseFormRegister<FieldValues>;
	setValue: UseFormSetValue<FieldValues>;
	formState: FormState<PLCustomerData>;
	trigger: (name?: string | string[]) => Promise<boolean>;
};

export const DateInputField: React.FC<DateInputFieldProps> = ({
	register,
	setValue,
	formState,
	trigger,
}) => {
	const customer = useSelector(plCustomerSelector);
	const { birthdate } = customer;
	const [isInitialized, setIsInitialized] = useState(false);
	const gaFunnelInfo = useGaFunnelInfo();

	// Single effect to handle initialization
	useEffect(() => {
		if (!isInitialized && customer?.birthdate) {
			const formattedDate = formatDateValue(customer.birthdate);
			if (formattedDate) {
				setValue('birthdate', formattedDate, {
					shouldDirty: false,
					shouldValidate: true,
				});
				setIsInitialized(true);
			}
		}
	}, [customer, setValue, isInitialized]);

	return (
		<FormField size="m" label={<Label text="Geboortedatum" reference="birthdate" required />}>
			<DateInput
				reference="birthdate"
				placeholder="dd-mm-jjjj"
				{...register('birthdate', {
					required: 'Je hebt je geboortedatum nog niet ingevuld.',
					validate: validateBirthdate,
				})}
				onChange={async (event: ChangeEvent<HTMLInputElement>) => {
					const newValue = event.target.value;
					setValue('birthdate', newValue, { shouldDirty: true });
					await trigger('birthdate');
					handleFormGaEvent('birthdate', formState, event, gaFunnelInfo);
				}}
				min="1900-01-01"
				max={getMaxBirthdate()}
				value={birthdate || ''}
			/>

			{formState.errors.birthdate && (
				<FormField.Error aria-live="assertive">
					{formState.errors.birthdate.message}
				</FormField.Error>
			)}
		</FormField>
	);
};
